import * as echarts from "echarts";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef, useState } from "react";

export interface IndexChartSeries {
  key: string;
  symbol: string;
  label: string;
  return_pct: number | null;
  values: (number | null)[];
  /** Actual index closing level per date, parallel to `values`. */
  closes: (number | null)[];
}

interface IndexComparisonChartProps {
  dates: string[];
  series: IndexChartSeries[];
  isLoading?: boolean;
  isDark?: boolean;
}

/** Fixed per-series colours so the legend chips and lines always agree. */
const SERIES_COLORS: Record<string, string> = {
  nifty: "#0ea5e9", // sky-500
  banknifty: "#f59e0b", // amber-500
};
const FALLBACK_COLOR = "#8b5cf6";
/** Chip colour once its series is toggled off (gray-400). */
const HIDDEN_CHIP_COLOR = "#9ca3af";

/** Helper series that draw the lead band; hidden from tooltip and labels. */
const BAND_PREFIX = "__band";

/**
 * Lines stay thin at rest and thin further on hover, so the crosshair and the
 * point being inspected are never crowded by a heavier stroke.
 */
const LINE_WIDTH = 1;
const LINE_WIDTH_HOVER = 0.6;

const formatPct = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value)
    ? "—"
    : `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

const formatClose = (value: number | null | undefined) =>
  value === null || value === undefined || Number.isNaN(value)
    ? "—"
    : value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const colorFor = (key: string) => SERIES_COLORS[key] ?? FALLBACK_COLOR;

/**
 * Not backed by real data yet — the strategy return this would plot isn't
 * defined (see the same placeholder in PerformanceStats). Temporarily hidden
 * until strategy returns are available.
 */
/*
const ABILLION_PERFORMANCE = {
  label: "Abillion Performance",
  tooltip: "Coming soon — Abillion's strategy return isn't defined yet",
};
*/

const IndexComparisonChart: React.FC<IndexComparisonChartProps> = ({
  dates,
  series,
  isLoading,
  isDark,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  const hasData = dates.length > 0 && series.length > 0;

  // Which series are plotted, toggled via the filter chips. Tracked as
  // "hidden" rather than "visible" so a series that arrives after the initial
  // render (data loads async) defaults to shown without extra syncing.
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());
  const toggleKey = (key: string) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleSeries = useMemo(
    () => series.filter((item) => !hiddenKeys.has(item.key)),
    [series, hiddenKeys],
  );

  /**
   * Which index is ahead over the range — drawn at full opacity. Ranked over
   * the plotted series only, so hiding the leader promotes whatever is left
   * rather than leaving every visible line de-emphasised.
   */
  const winnerKey = useMemo(() => {
    const ranked = visibleSeries
      .filter((item) => item.return_pct !== null)
      .sort((a, b) => (b.return_pct ?? 0) - (a.return_pct ?? 0));

    return ranked[0]?.key ?? null;
  }, [visibleSeries]);

  const option = useMemo<echarts.EChartsOption>(() => {
    const axisColor = isDark ? "#394564" : "#e2e8f0";
    const textColor = isDark ? "#94a3b8" : "#64748b";

    // The band between the two lines, tinted with whichever is on top, so
    // "who is ahead" reads instantly instead of being inferred from position.
    // Only draws once both toggled-on series are present.
    const first = visibleSeries[0];
    const second = visibleSeries[1];
    const bandBase: (number | null)[] = [];
    const bandFirstLead: (number | null)[] = [];
    const bandSecondLead: (number | null)[] = [];

    if (first && second) {
      dates.forEach((_, index) => {
        const a = first.values[index] ?? null;
        const b = second.values[index] ?? null;
        if (a === null || b === null) {
          bandBase.push(null);
          bandFirstLead.push(null);
          bandSecondLead.push(null);
          return;
        }
        bandBase.push(Math.min(a, b));
        const gap = Math.abs(a - b);
        bandFirstLead.push(a >= b ? gap : 0);
        bandSecondLead.push(a >= b ? 0 : gap);
      });
    }

    // Returns go negative, and ECharts' default "samesign" stacking keeps
    // positive and negative values in separate stacks, which would draw the gap
    // up from 0% instead of on top of the lower line. "all" sums them.
    const bandSeries =
      first && second
        ? [
            {
              name: `${BAND_PREFIX}_base`,
              type: "line" as const,
              stack: "band",
              stackStrategy: "all" as const,
              data: bandBase,
              symbol: "none" as const,
              lineStyle: { width: 0, opacity: 0 },
              areaStyle: { color: "transparent", opacity: 0 },
              silent: true,
              z: 1,
            },
            {
              name: `${BAND_PREFIX}_first`,
              type: "line" as const,
              stack: "band",
              stackStrategy: "all" as const,
              data: bandFirstLead,
              symbol: "none" as const,
              lineStyle: { width: 0, opacity: 0 },
              areaStyle: { color: `${colorFor(first.key)}14` },
              silent: true,
              z: 1,
            },
            {
              name: `${BAND_PREFIX}_second`,
              type: "line" as const,
              stack: "band",
              stackStrategy: "all" as const,
              data: bandSecondLead,
              symbol: "none" as const,
              lineStyle: { width: 0, opacity: 0 },
              areaStyle: { color: `${colorFor(second.key)}14` },
              silent: true,
              z: 1,
            },
          ]
        : [];

    return {
      animationDuration: 400,
      // No x labels: the header carries the range and the tooltip the exact
      // date, so the ~18px they cost goes to the plot instead.
      grid: { top: 14, right: 58, bottom: 4, left: 2, containLabel: true },
      tooltip: {
        trigger: "axis",
        backgroundColor: isDark ? "#1A1F29" : "#ffffff",
        borderColor: isDark ? "#2D3442" : "#e2e8f0",
        borderWidth: 1,
        textStyle: { color: isDark ? "#e2e8f0" : "#0f172a", fontSize: 11 },
        axisPointer: {
          type: "line",
          lineStyle: { color: textColor, type: "dashed", width: 1 },
        },
        formatter: (params) => {
          const list = (Array.isArray(params) ? params : [params]).filter(
            (item) =>
              !String(
                (item as { seriesName?: string }).seriesName ?? "",
              ).startsWith(BAND_PREFIX),
          );
          if (list.length === 0) return "";
          const header = moment(
            (list[0] as unknown as { axisValue: string }).axisValue,
          ).format("DD MMM YYYY");
          const body = list
            .map((item) => {
              const point = item as {
                marker?: string;
                seriesName?: string;
                value?: number | null;
                dataIndex: number;
              };
              // The line plots the rebased %, but the close level is what a
              // user actually recognises — show both on hover.
              const close = visibleSeries.find(
                (s) => s.label === point.seriesName,
              )?.closes?.[point.dataIndex];
              return `<div style="display:flex;align-items:center;gap:6px;justify-content:space-between">
                  <span>${point.marker ?? ""}${point.seriesName ?? ""}</span>
                  <span style="margin-left:12px;display:flex;align-items:baseline;gap:6px">
                    <span style="opacity:0.7">${formatClose(close)}</span>
                    <b>${formatPct(point.value ?? null)}</b>
                  </span>
                </div>`;
            })
            .join("");
          return `<div style="font-weight:600;margin-bottom:4px">${header}</div>${body}`;
        },
      },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "value",
        scale: true,
        splitNumber: 3,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: { color: axisColor, type: "dashed", opacity: 0.7 },
        },
        axisLabel: {
          color: textColor,
          fontSize: 10,
          margin: 6,
          formatter: (value: number) => `${value > 0 ? "+" : ""}${value}%`,
        },
      },
      series: [
        ...bandSeries,
        ...visibleSeries.map((item, index) => {
          const color = colorFor(item.key);
          const isWinner = item.key === winnerKey;
          return {
            name: item.label,
            type: "line" as const,
            data: item.values,
            showSymbol: false,
            symbolSize: 7,
            smooth: 0.25,
            connectNulls: true,
            sampling: "lttb" as const,
            z: isWinner ? 4 : 3,
            // Thin at rest; the leading index reads through opacity rather
            // than a heavier stroke.
            lineStyle: {
              width: LINE_WIDTH,
              color,
              opacity: isWinner ? 1 : 0.85,
            },
            itemStyle: {
              color,
              borderColor: isDark ? "#121419" : "#fff",
              borderWidth: 2,
            },
            // Hovering thins every line: the hovered series through emphasis,
            // the rest through blur — which echarts only applies when `focus`
            // is set, hence it being here. Blur repeats the resting opacity so
            // the others thin with it rather than also fading out, leaving no
            // line singled out by weight while a point is inspected.
            emphasis: {
              focus: "series" as const,
              scale: false,
              lineStyle: { width: LINE_WIDTH_HOVER },
            },
            blur: {
              lineStyle: {
                width: LINE_WIDTH_HOVER,
                opacity: isWinner ? 1 : 0.85,
              },
            },
            // Only the last series carries the zero baseline, so it is drawn once.
            markLine:
              index === visibleSeries.length - 1
                ? {
                    silent: true,
                    symbol: "none",
                    label: {
                      show: true,
                      position: "start" as const,
                      formatter: "0%",
                      fontSize: 9,
                      color: textColor,
                      backgroundColor: isDark ? "#121419" : "#ffffff",
                      padding: [1, 3],
                    },
                    lineStyle: {
                      color: textColor,
                      type: "dashed" as const,
                      width: 1,
                      opacity: 0.7,
                    },
                    data: [{ yAxis: 0 }],
                  }
                : undefined,
            endLabel: {
              show: true,
              distance: 6,
              color: "#ffffff",
              backgroundColor: color,
              padding: [3, 5],
              borderRadius: 4,
              fontSize: 10,
              fontWeight: "bold" as const,
              formatter: () => formatPct(item.return_pct),
            },
          };
        }),
      ],
    };
  }, [dates, visibleSeries, isDark, winnerKey]);

  // Initialise once and dispose only on unmount. The container is never
  // conditionally swapped, so React never tries to remove the canvas ECharts
  // owns — doing so throws "removeChild ... not a child of this node".
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const chart = echarts.init(element, undefined, { renderer: "canvas" });
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Updates go through setOption rather than a re-init.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chart.isDisposed()) return;

    if (!hasData) {
      chart.clear();
      return;
    }

    chart.setOption(option, true);
    chart.resize();
  }, [option, hasData]);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-[#121419]">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-2 py-1">
        <h3 className="text-sm font-medium text-[#3D3D3D] dark:text-white">
          Index Comparison
        </h3>

        {/*
          Toggle chips: click to show/hide a series, nothing more — the
          closing level belongs to the hover tooltip. Held back until there is
          a chart to toggle.
        */}
        <div
          className={`flex flex-wrap items-center gap-1.5 ${
            hasData ? "" : "hidden"
          }`}
        >
          {series.map((item) => {
            const isHidden = hiddenKeys.has(item.key);
            const color = colorFor(item.key);
            return (
              <button
                key={item.key}
                type="button"
                aria-pressed={!isHidden}
                onClick={() => toggleKey(item.key)}
                title={`${isHidden ? "Show" : "Hide"} ${item.label}`}
                className="flex items-center gap-1.5 truncate rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors"
                style={
                  isHidden
                    ? {
                        borderColor: "transparent",
                        color: HIDDEN_CHIP_COLOR,
                        backgroundColor: "transparent",
                      }
                    : {
                        borderColor: `${color}40`,
                        color,
                        backgroundColor: `${color}1F`,
                      }
                }
              >
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor: isHidden ? HIDDEN_CHIP_COLOR : color,
                  }}
                />
                {item.label}
              </button>
            );
          })}

          {/* Temporarily hidden until Abillion strategy returns are available.
          <span
            title={ABILLION_PERFORMANCE.tooltip}
            aria-disabled="true"
            className="flex cursor-not-allowed items-center gap-1.5 truncate rounded-full border border-dashed border-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-400 dark:border-[#2D3442] dark:text-gray-500"
          >
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-gray-600" />
            {ABILLION_PERFORMANCE.label}
          </span>
          */}
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        {/* Always mounted — the skeleton and empty state overlay it. */}
        <div
          ref={containerRef}
          className={`h-full w-full transition-opacity duration-200 ${
            isLoading && hasData ? "opacity-60" : "opacity-100"
          }`}
        />

        {/* Only the first load (no chart drawn yet) gets the bar skeleton —
            a refetch from a filter change dims the chart above instead of
            covering it, so the axes never visibly disappear and reappear. */}
        {isLoading && !hasData && (
          <div className="absolute inset-0 flex items-end gap-1 bg-white p-2 dark:bg-[#121419]">
            {Array.from({ length: 28 }).map((_, i) => (
              <div
                key={i}
                className="flex-1 animate-pulse rounded-sm bg-gray-100 dark:bg-white/5"
                style={{ height: `${25 + ((i * 37) % 60)}%` }}
              />
            ))}
          </div>
        )}

        {!isLoading && !hasData && (
          <div className="absolute inset-0 flex items-center justify-center bg-white text-xs text-gray-400 dark:bg-[#121419] dark:text-gray-500">
            No index data for this range
          </div>
        )}
      </div>
    </div>
  );
};

export default IndexComparisonChart;
